import React, { useState, useMemo } from 'react'
import { DialogContent } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import StyledDialog from '../../Common/Dialogs/StyledDialog'
import { MainButton } from '../../Button/MainButton'
import useSelectedFacility from '../../../hooks/useSelectedFacility'
import StyledAlert from '../../Common/StyledAlert'
import CheckboxMenu from '../../Common/Menus/CheckboxMenu'
import PetAvatar from '../../Common/PetAvatar'
import TrainingClassEnrollmentsAutocomplete from '../../Autocompletes/TrainingClassEnrollments'
import StyledTextField from '../../Common/StyledTextField'

const useStyles = makeStyles(() => ({
	dialogContent: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		padding: '0 1.5rem',
		gap: '1rem',
		flex: 1
	},
	rowWrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '2rem'
	},
	dialogActions: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'flex-start',
		gap: '1rem',
		margin: '2rem 0 1rem 0',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		},
		justifySelf: 'flex-end'
	},
	petAvatarContainer: {
		display: 'flex',
		gap: '1rem',
		alignItems: 'center'
	}
}))

/**
 *
 * @param {{
  open: Boolean, 
	onClose: Function, 
	onCancelled: Function,
	title: String,
	trainingClass: Object,
	session: Object
}} params
 * @returns
 */
const TrainingSessionAddAttendeeDialog = ({
	open,
	onSaved,
	onCancelled,
	title = 'Add Attendee',
	trainingClass,
	session
}) => {
	const classes = useStyles()

	const [editedEnrollment, setEditedEnrollment] = useState({})
	const [error, setError] = useState(null)
	const [loading, setLoading] = useState(false)
	const { facility, saveAttendanceTransferForClass } = useSelectedFacility()

	const handleResetDialog = () => {
		setEditedEnrollment({})
		setError(null)
	}

	const handleOnCancelled = () => {
		if (onCancelled) {
			handleResetDialog()
			onCancelled()
		}
	}

	const handleOnSave = async () => {
		const { primary_handler, household, pet_ids, availableHouseholdPets } = editedEnrollment
		if (!primary_handler) {
			setError('Primary account is not selected.')
			return
		}

		const transfers = []

		for (const pet_id of pet_ids) {
			const selectedPet = availableHouseholdPets.find((pet) => pet._id === pet_id)
			const transfer = {
				from_class_id: selectedPet.training_class_id,
				to_class_id: trainingClass._id,
				pet_id: selectedPet._id,
				accounts: selectedPet.handlers.map((handler) => handler._id),
				household_id: household._id,
				session_number: session?.session,
				session_date: session?.session_date,
				enrollment_id: selectedPet.enrollmentId
			}
			transfers.push(transfer)
		}

		setLoading(true)

		await saveAttendanceTransferForClass({
			facility_id: facility?._id,
			mongo_class_id: trainingClass._id,
			transfers
		})

		setLoading(false)

		if (onSaved) {
			handleResetDialog()
			onSaved()
		}
	}

	const handleFieldUpdate = ({ field, targetValue }) => {
		const [mainField, subField] = field.split('.')
		setEditedEnrollment((prev) => {
			const revised = { ...prev }
			if (subField) {
				revised[mainField] = { ...revised[mainField], [subField]: targetValue }
			} else {
				revised[field] = targetValue
			}

			return revised
		})
	}

	const handleHouseholdSelected = (h) => {
		const { household, pets } = h || { household: null, pets: [] }
		const otherAccountsMap = new Map()
		let secondary_handler = null
		let secondary_handler_id = null
		for (const pet of pets) {
			for (const handler of pet.handlers) {
				if (handler._id !== household?.account._id) {
					secondary_handler = handler
					secondary_handler_id = handler._id
					otherAccountsMap.set(handler._id, handler)
				}
			}
		}
		setEditedEnrollment(() => {
			return {
				primary_handler: household?.account,
				secondary_handler,
				secondary_handler_id,
				household,
				availableHouseholdPets: pets,
				otherHouseholdAccounts: Array.from(otherAccountsMap.values())
			}
		})
	}

	const petOptions = useMemo(() => {
		if (!editedEnrollment.availableHouseholdPets) {
			return []
		}

		const options = editedEnrollment.availableHouseholdPets.map((option) => {
			const { name, image_url, profile_image_file_name, _id } = option

			return {
				component: (
					<div className={classes.petAvatarContainer}>
						<PetAvatar petName={name} imageUrl={image_url || profile_image_file_name} />
					</div>
				),
				value: _id
			}
		})
		return options
	}, [editedEnrollment.availableHouseholdPets, trainingClass])

	const currentlyEnrolledPetsInSession = useMemo(() => {
		const enrolledPetsSet = new Set()
		if (!session || !session.attendance) {
			return enrolledPetsSet
		}
		for (const attendance of session.attendance) {
			const pet = attendance.pet[0]
			enrolledPetsSet.add(pet._id)
		}
		return enrolledPetsSet
	}, [session])

	const canSave = useMemo(() => {
		const { pet_ids } = editedEnrollment
		return pet_ids && pet_ids.length > 0
	}, [editedEnrollment])

	return (
		<>
			<StyledDialog
				open={open}
				onClose={handleOnCancelled}
				title={title}
				showActivityIndicator={loading}
				minHeight={500}
				minWidth={500}
				maxWidth={500}>
				<DialogContent style={{ display: 'flex' }}>
					<div className={classes.dialogContent}>
						<div className={classes.rowWrapper}>
							<TrainingClassEnrollmentsAutocomplete
								onHouseholdSelected={(household) => handleHouseholdSelected(household)}
								displayAddress={false}
								additionalSearchParams={[
									['searchBy', 'contact'],
									['trainingCourseId', trainingClass?.training_course_id?._id]
								]}
								excludePetIds={currentlyEnrolledPetsInSession}
							/>
							{editedEnrollment.secondary_handler ? (
								<>
									<StyledTextField
										viewonly
										value={editedEnrollment.secondary_handler.full_name || ''}
										header="Additional Client"
										label="Additional Client"
									/>
								</>
							) : null}
							<CheckboxMenu
								header={
									editedEnrollment.availableHouseholdPets?.length > 1
										? 'Select Pet(s)*'
										: 'Select Pet*'
								}
								subheader={`Select which ${
									editedEnrollment.availableHouseholdPets?.length > 1 ? 'pet(s)' : 'pet'
								} to transfer to this session.`}
								maxSelections={editedEnrollment.availableHouseholdPets?.length || 1}
								options={petOptions}
								onSelectedChanged={(v) => {
									handleFieldUpdate({
										field: 'pet_ids',
										targetValue: v
									})
								}}
							/>
							{error ? <StyledAlert type="error" text={error} /> : null}
						</div>
						<div className={classes.dialogActions}>
							<MainButton
								label="Confirm"
								onClick={handleOnSave}
								variant="contained"
								disabled={!canSave}
							/>
							<MainButton label="Cancel" onClick={handleOnCancelled} />
						</div>
					</div>
				</DialogContent>
			</StyledDialog>
		</>
	)
}

export default TrainingSessionAddAttendeeDialog
